import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useCurrentUser } from '../shared/hooks/useCurrentUser.js';
import { useLogout } from '../shared/hooks/useLogout.js';
import { LogOutIcon } from '../shared/icons.jsx';

/**
 * Minimal /settings page per UI_FRAMEWORK Q5:
 *   display-only account info + Sign out.
 * Timezone, role changes, permissions all admin-managed.
 */
export function Settings() {
  const { data: user } = useCurrentUser();
  const navigate = useNavigate();
  const logout = useLogout();

  if (!user) return null;

  function handleLogout() {
    logout.mutate(undefined, {
      onSuccess: () => navigate('/login', { replace: true }),
      onError: (err) => toast.error(err.message || 'Logout failed'),
    });
  }

  const moduleRoleList = Object.entries(user.moduleRoles || {});

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-ink-900">My Account</h1>
        <p className="text-sm text-ink-500">
          Everything here is admin-managed. Ask an admin to change roles or building access.
        </p>
      </div>

      <section className="rounded-xl border border-sky-300 bg-white p-6 shadow-sm">
        <div className="flex items-center gap-4">
          {user.picture ? (
            <img src={user.picture} alt="" className="h-16 w-16 rounded-full" />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-600 text-lg font-semibold text-white">
              {(user.name || '?').slice(0, 1)}
            </div>
          )}
          <div>
            <div className="text-lg font-semibold text-ink-900">{user.name}</div>
            <div className="text-sm text-ink-500">{user.email}</div>
          </div>
        </div>

        <dl className="mt-6 grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
          <div>
            <dt className="text-ink-500">Job title</dt>
            <dd className="text-ink-900">{user.job_title || '—'}</dd>
          </div>
          <div>
            <dt className="text-ink-500">Global role</dt>
            <dd className="text-ink-900">
              {user.global_role === 'admin' ? 'Superadmin' : 'User'}
            </dd>
          </div>
        </dl>
      </section>

      <section className="rounded-xl border border-sky-300 bg-white p-6 shadow-sm">
        <h2 className="mb-3 text-lg font-semibold text-ink-900">Access</h2>

        <div className="mb-4">
          <div className="mb-2 text-xs font-semibold uppercase text-ink-500">Buildings</div>
          {user.buildings?.length ? (
            <ul className="flex flex-wrap gap-2">
              {user.buildings.map((b) => (
                <li
                  key={b.id}
                  className="rounded-lg bg-sky-200 px-3 py-1 text-xs font-semibold uppercase text-ink-600"
                >
                  {b.code}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-ink-500">None.</p>
          )}
        </div>

        <div>
          <div className="mb-2 text-xs font-semibold uppercase text-ink-500">Module roles</div>
          {moduleRoleList.length ? (
            <ul className="space-y-1">
              {moduleRoleList.map(([mod, role]) => (
                <li key={mod} className="flex justify-between text-sm">
                  <span className="capitalize text-ink-900">{mod}</span>
                  <span className="rounded bg-blue-600/10 px-2 py-0.5 text-xs font-semibold uppercase text-blue-700">
                    {role}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-ink-500">None.</p>
          )}
        </div>
      </section>

      <section className="rounded-xl border border-sky-300 bg-white p-6 shadow-sm">
        <button
          type="button"
          onClick={handleLogout}
          disabled={logout.isPending}
          className="inline-flex items-center gap-2 rounded-md bg-sky-200 px-6 py-3 text-sm font-semibold uppercase text-ink-600 transition-colors hover:bg-sky-300 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2"
        >
          <LogOutIcon className="h-4 w-4" />
          {logout.isPending ? 'Signing out…' : 'Sign out'}
        </button>
      </section>
    </div>
  );
}
